import { useCallback } from 'react';

import { ScannerStatusResponse } from '@ariksa/data-scanning/api';
import { each, some } from 'lodash';
import isArray from 'lodash/isArray';
import { useDispatch, useSelector } from 'react-redux';

import { selectApp } from 'containers/App/selectors';
import { actions } from 'containers/SharedState/slice';

export const useScannerInfo = () => {
  const dispatch = useDispatch();
  const { environments, cloudAccounts } = useSelector(selectApp);

  const onSuccessDataScanner = useCallback(
    (res?: ScannerStatusResponse) => {
      let items: Record<string, boolean> = {};
      const scannerAccountIds = new Set(
        [
          ...Object.keys(res?.scanners ?? {}),
          ...Object.values(res?.scanners ?? {}).flatMap(scanners =>
            (scanners ?? []).map(scanner => scanner.account_id),
          ),
          ...(res?.assignments ?? []).map(
            assignment => assignment.non_cloud_account_id,
          ),
        ].filter((id): id is string => !!id),
      );
      const deployedAccountIds = new Set(scannerAccountIds);

      each(cloudAccounts.data, account => {
        const isDeployed =
          scannerAccountIds.has(account.uuid) ||
          (!!account.cloud_account_id &&
            scannerAccountIds.has(account.cloud_account_id));

        items[account.uuid] = isDeployed;
        if (account.cloud_account_id)
          items[account.cloud_account_id] = isDeployed;

        if (isDeployed) {
          deployedAccountIds.add(account.uuid);
          if (account.cloud_account_id)
            deployedAccountIds.add(account.cloud_account_id);
        }
      });

      each(environments.data, environment => {
        items[environment.id] = some(environment.account_ids ?? [], accountId =>
          deployedAccountIds.has(accountId as string),
        );
      });

      dispatch(actions.updateIsDataScannerDeployed(items));
    },
    [dispatch, environments.data, cloudAccounts.data],
  );

  const onSuccessVulnerabilityScanner = useCallback(
    (res?: any) => {
      let items: Record<string, boolean> = {};
      const scannerAccountIds = new Set<string>();

      if (isArray(res)) {
        each(environments.data, environment => {
          items[environment.id] = true;
        });
      } else {
        // ScanVault returns scanner statuses grouped by account UUID. Keep
        // the deployment map keyed by the same UUIDs used by environments and
        // the access boundary so consumers do not depend on response shape.
        each(Object.keys(res ?? {}), accountId => {
          if (accountId) scannerAccountIds.add(String(accountId));
        });

        each(Object.values(res ?? {}), scanners => {
          each(scanners as any[], scanner => {
            if (scanner?.cloud_account_id) {
              scannerAccountIds.add(String(scanner.cloud_account_id));
            }
            if (scanner?.account_uuid) {
              scannerAccountIds.add(String(scanner.account_uuid));
            }
          });
        });

        const deployedAccountIds = new Set(scannerAccountIds);
        each(cloudAccounts.data, account => {
          const accountIds = [account.uuid, account.cloud_account_id].filter(
            (id): id is string => !!id,
          );
          const isDeployed = accountIds.some(id =>
            scannerAccountIds.has(String(id)),
          );

          accountIds.forEach(id => {
            items[String(id)] = isDeployed;
            if (isDeployed) deployedAccountIds.add(String(id));
          });
        });

        each(environments.data, environment => {
          items[environment.id] = some(
            environment.account_ids ?? [],
            accountId => deployedAccountIds.has(String(accountId)),
          );
        });
      }

      dispatch(actions.updateIsVulnerabilityScannerDeployed(items));
    },
    [dispatch, environments.data, cloudAccounts.data],
  );

  return {
    onSuccessDataScanner,
    onSuccessVulnerabilityScanner,
  };
};
